import * as Sentry from '@sentry/react';
import { BrowserTracing } from '@sentry/tracing';

const SENTRY_DSN = import.meta.env.VITE_SENTRY_DSN;
const SENTRY_ENVIRONMENT = import.meta.env.VITE_SENTRY_ENVIRONMENT || import.meta.env.MODE || 'development';
const SENTRY_RELEASE = import.meta.env.VITE_SENTRY_RELEASE || 'unknown';

// Only initialize Sentry if DSN is provided
if (SENTRY_DSN) {
  Sentry.init({
    dsn: SENTRY_DSN,
    environment: SENTRY_ENVIRONMENT,
    release: SENTRY_RELEASE,

    // Performance Monitoring
    integrations: [
      new BrowserTracing({
        // Set sampling rate for navigation/route transactions
        tracePropagationTargets: ['localhost', /^\//],
      }),
      new Sentry.Replay({
        // Capture 10% of all sessions, 100% of sessions with errors
        sessionSampleRate: 0.1,
        errorSampleRate: 1.0,
        maskAllText: true,
        blockAllMedia: true,
      }),
    ],

    // Set sample rates
    tracesSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.1 : 1.0, // 10% in prod, 100% in dev
    replaysSessionSampleRate: SENTRY_ENVIRONMENT === 'production' ? 0.1 : 0.5,
    replaysOnErrorSampleRate: 1.0, // Always capture replays for errors

    // BeforeSend hook to filter and sanitize data
    beforeSend(event, _hint) {
      // Filter out sensitive data from breadcrumbs
      if (event.breadcrumbs) {
        event.breadcrumbs = event.breadcrumbs.map((breadcrumb) => {
          if (breadcrumb.data) {
            // Remove authorization tokens
            if (breadcrumb.data.authorization) {
              breadcrumb.data.authorization = '***';
            }
            // Remove password fields
            if (breadcrumb.data.password) {
              breadcrumb.data.password = '***';
            }
          }
          return breadcrumb;
        });
      }

      // Filter out local storage/session storage data
      if (event.contexts?.['browser']) {
        delete event.contexts['browser'];
      }

      // Sample errors in development to save quota
      if (SENTRY_ENVIRONMENT === 'development' && Math.random() > 0.3) {
        return null; // Send only 30% of errors in dev
      }

      return event;
    },

    // Ignore common non-error cases
    ignoreErrors: [
      // Random plugins/extensions
      'top.GLOBALS',
      'originalCreateNotification',
      'canvas.contentDocument',
      'MyApp_RemoveAllHighlights',
      // Browser extensions
      'Can\'t find variable: ZiteReader',
      'jigsaw is not defined',
      'ComboSearch is not defined',
      // Network errors that are user issues, not bugs
      'NetworkError',
      'Network request failed',
      'Failed to fetch',
      // ResizeObserver errors (benign)
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications',
      // Google Maps WKWebView postMessage timeout (benign on iOS)
      'WKWebView API client did not respond to this postMessage',
      // Browser-extension noise (e.g. Office/Outlook Safe Links) - not our code, no stacktrace
      'Object Not Found Matching Id',
      'Non-Error promise rejection captured',
    ],

    // Don't send PII by default
    sendDefaultPii: false,
  });

  if (import.meta.env.DEV) {
    console.debug(`[Sentry] Initialized for environment: ${SENTRY_ENVIRONMENT}`);
  }
}

export default Sentry;
